import {
    Form,
    FormField,
    FormUploadButton,
    FormFieldConditions,
    FormSection,
    FormType,
    DropdownData,
    InputType,
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { list } from '@fitogest/reactive-form-builder/utils';
import { ClienteModel } from 'src/app/models/cliente.model';
import { Compradores } from 'src/app/models/ventas/compradores.model';
import { MaquinariaModel } from 'src/app/models/trabajadores/maquinaria.model';
import { environment } from 'src/environments/environment';
import moment from 'moment';

export class MaquinariaListsDefinition {
    public clientes: DropdownData<ClienteModel> = list<ClienteModel>();

    public comercios: DropdownData<Compradores> = list<Compradores>(['...', null]);

    public tipos: DropdownData<string> = list<string>(
        ['...', null],
        'Abonadora',
        'Apero de preparación de suelo',
        ['Atomizador/Turboatomizador', 'Turboatomizador'],
        'Carretilla de fumigar',
        'Coche',
        'Cosechadora',
        'Desbrozadora Manual',
        ['Cuba/Herbicida', 'Herbicida'],
        ['Cuba con pistolete/Disparador normal', 'Disparador'],
        'Mochila',
        'Mochila para fumigar',
        'Parcheo',
        'Pulverizadora',
        'Remolque esparcidor de basura',
        'Sembradora',
        'Tractor',
    );
}

export class MaquinariaFormDefinition {
    private readonly formType!: FormType;

    public readonly lists: MaquinariaListsDefinition = new MaquinariaListsDefinition();

    public readonly appName = environment.appName;

    public readonly clienteName = environment.features.clienteName;

    public readonly activateREA = environment.features.activateREA;

    public readonly showAplicadoresActivo = environment.features.showAplicadoresActivo;

    public readonly showMaquinariaAdjuntoPdf = environment.features.showMaquinariaAdjuntoPdf;

    public readonly showMaquinariaClienteAsignado = environment.features.showMaquinariaClienteAsignado;

    public readonly showMaquinariaGps = environment.features.showMaquinariaGps;

    public readonly showMaquinariaVidaUtil = environment.features.showMaquinariaVidaUtil;

    public readonly precioHora = environment.features.precioHora;

    public readonly titleActivo = environment.features.titleActivoMaqTra;

    public model: ReactiveFormValue<MaquinariaModel> = {} as ReactiveFormValue<MaquinariaModel>;

    public lastChangedFieldName: ReactiveFormChangedField<MaquinariaModel> = '';

    public formFields!: Form<MaquinariaModel>;

    public fieldNames: FormField<MaquinariaModel>[] = [];

    public fieldLabels: string[] = [];

    /**
     * Creates the machinery form definition shared by edit forms and list modals.
     *
     * The definition centralizes machinery sections, dropdown lists, visibility
     * rules and hidden controls so all machinery modes use the same source of truth.
     *
     * @constructor
     * @public
     *
     * @param type - Request type used to define the form behaviour.
     * @param model - Optional machinery model used as initial form data.
     * @param table - Optional table configuration used by table-based form modes.
     *
     * @returns A new machinery form definition instance.
     */
    constructor(
        type: FormType,
        model: Partial<MaquinariaModel> | null = {},
        table: (string[] | string[][])[] | null = null,
    ) {
        this.formType = +type;
        this.model = {
            ...this.model,
            ...(model ?? {}),
        } as ReactiveFormValue<MaquinariaModel>;
        this.normalizeModel();
        this.formFields = this.buildForm(table);
    }// constructor();

    /**
     * Updates the stored model and rebuilds visible section metadata.
     *
     * @public
     *
     * @param model - Partial machinery model received from the reactive form.
     * @param lastChangedFieldName - Last field changed by the user or by the builder.
     *
     * @returns Nothing.
     */
    public updateModel(
        model: ReactiveFormValue<MaquinariaModel>,
        lastChangedFieldName: ReactiveFormChangedField<MaquinariaModel>,
    ): void {
        this.model = {
            ...this.model,
            ...model,
        } as ReactiveFormValue<MaquinariaModel>;
        this.lastChangedFieldName = lastChangedFieldName;

        if (lastChangedFieldName === 'fecha_adquisicion' || lastChangedFieldName === 'fecha_ultima_inspeccion') {
            this.calculateExpirationDate();
        }// if();

        this.normalizeModel();
        this.refreshSections();
    }// updateModel();

    /**
     * Rebuilds section visibility and field metadata without replacing dropdown lists.
     *
     * @public
     *
     * @returns Nothing.
     */
    public refreshSections(): void {
        this.fieldNames = [];
        this.fieldLabels = [];
        this.formFields = {
            ...this.formFields,
            sections: this.getFormSections(),
            uploadButtons: this.getUploadButtons(),
        };
    }// refreshSections();

    /**
     * Builds and returns the main machinery fields.
     *
     * @public
     *
     * @returns The machinery fields sorted by display order.
     */
    public getMachineryFields(): FormField<MaquinariaModel>[] {
        const machineryFields: FormField<MaquinariaModel>[] = [
            {
                name: 'nombre',
                label: 'Nombre, Marca y Modelo',
                input: { type: InputType.TEXT, isEditable: this.isEditable() },
                width: 3,
                conditions: this.conditions({ isRequired: true }),
                order: 1,
            },
            {
                name: 'tipo',
                label: 'Tipo',
                input: {
                    type: this.isEditable() ? InputType.DROPDOWN : InputType.ONLY_TEXT,
                    isEditable: this.isEditable(),
                    data: this.lists.tipos,
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                width: 3,
                conditions: this.conditions(),
                order: 2,
            },
            {
                name: 'capacidad',
                label: 'Capacidad (Litros)',
                input: { type: InputType.NUMERIC, isEditable: this.isEditable() },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'detorres' }),
                order: 3,
            },
            {
                name: 'roma',
                label: 'ROMA',
                input: { type: InputType.TEXT, isEditable: this.isEditable() },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'detorres' }),
                order: 4,
            },
            {
                name: this.getClientFieldName(),
                label: this.appName === 'innovia' ? this.clienteName + ' asignado' : 'Propietario',
                input: {
                    type: this.isEditable() ? InputType.DROPDOWN_SEARCH : InputType.ONLY_TEXT,
                    isEditable: this.isEditable(),
                    data: this.lists.clientes,
                    dropdownConfiguration: { valuePrimaryKey: 'id' },
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.showMaquinariaClienteAsignado }),
                order: 5,
            },
            {
                name: 'fecha_adquisicion',
                label: 'Fecha Adquisición',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: this.isEditable(),
                    calendarConfiguration: { calendarView: 'date', calendarDateFormat: 'dd/mm/yy' },
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName !== 'detorres' }),
                order: 6,
            },
            {
                name: 'fecha_ultima_inspeccion',
                label: 'Fecha Última Inspección ITEAF',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: this.isEditable(),
                    calendarConfiguration: { calendarView: 'date', calendarDateFormat: 'dd/mm/yy' },
                },
                width: 3,
                conditions: this.conditions(),
                order: 7,
            },
            {
                name: 'fecha_caducidad',
                label: 'Fecha Caducidad ITEAF',
                input: { type: InputType.ONLY_TEXT, isEditable: false, style: 'color: black' },
                width: 3,
                conditions: this.conditions(),
                order: 8,
            },
            {
                name: 'fecha_caducidad_itv',
                label: 'Fecha Caducidad ITV',
                input: { type: InputType.CALENDAR, isEditable: true, style: 'color: black' },
                width: 3,
                conditions: this.conditions({isVisible: this.appName === 'viverosmuzale'}),
                order: 8,
            },
            {
                name: 'fecha_alerta',
                label: 'Fecha Alerta',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: this.isEditable(),
                    calendarConfiguration: { calendarView: 'date', calendarDateFormat: 'dd/mm/yy' },
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'agrotata' }),
                order: 9,
            },
            {
                name: 'fecha_ultimo_mantenimiento',
                label: 'Fecha Último Mantenimiento',
                input: {
                    type: InputType.CALENDAR,
                    isEditable: this.isEditable(),
                    calendarConfiguration: { calendarView: 'date', calendarDateFormat: 'dd/mm/yy' },
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'detorres' }),
                order: 10,
            },
            {
                name: 'vida_util',
                label: 'Vida Útil Estimada (Horas)',
                input: { type: InputType.NUMERIC, isEditable: this.isEditable() },
                width: 3,
                conditions: this.conditions({ isVisible: this.showMaquinariaVidaUtil }),
                order: 11,
            },
            {
                name: 'precio',
                label: 'Precio (€)',
                input: { type: InputType.NUMERIC, isEditable: this.isEditable() },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'detorres' }),
                order: 12,
            },
            {
                name: 'precio_hora',
                label: 'Coste/hora (€)',
                input: { type: InputType.NUMERIC, isEditable: this.isEditable() },
                width: 3,
                conditions: this.conditions({ isVisible: this.precioHora }),
                order: 13,
            },
            {
                name: this.getCommerceFieldName(),
                label: this.appName === 'sirga' ? 'Proveedor' : 'Empresa Servicios Agrícolas',
                input: {
                    type: this.isEditable() ? InputType.DROPDOWN : InputType.ONLY_TEXT,
                    isEditable: this.isEditable(),
                    data: this.lists.comercios,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (option) => this.filterCommerceOption(option.value),
                    },
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                width: 3,
                conditions: this.conditions({ isVisible: environment.features.showVentas || this.appName === 'covidai' || this.appName === 'sirga' }),
                order: 14,
            },
            {
                name: 'gps',
                label: 'Identificador GPS',
                input: { type: InputType.TEXT, isEditable: this.isEditable() },
                width: 3,
                conditions: this.conditions({ isVisible: this.showMaquinariaGps }),
                order: 15,
            },
            {
                name: 'imei',
                label: 'IMEI',
                input: { type: InputType.TEXT, isEditable: this.isEditable() },
                width: 3,
                conditions: this.conditions({ isVisible: this.showMaquinariaGps }),
                order: 16,
            },
            {
                name: 'activo',
                label: this.getActiveLabel(),
                input: { type: InputType.SWITCH, isEditable: this.isEditable() },
                width: 3,
                conditions: this.conditions({ isVisible: this.showAplicadoresActivo }),
                order: 17,
            },
            {
                name: 'mail_trat',
                label: 'Recibir mail tratamientos',
                input: { type: InputType.SWITCH, isEditable: this.isEditable() },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'manezylozano' }),
                order: 18,
            },
            {
                name: 'observaciones',
                label: 'Observaciones',
                input: {
                    type: InputType.TEXTAREA,
                    isEditable: this.isEditable(),
                    textAreaConfiguration: { rows: 8, hasVoiceTranscription: true, canTakePhoto: false, photoField: undefined },
                },
                width: 3,
                conditions: this.conditions({ isVisible: this.appName === 'detorres' }),
                order: 19,
            },
        ];

        this.appendVisibleFields(machineryFields);
        return machineryFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getMachineryFields();

    /**
     * Builds hidden fields required by submit and delete actions.
     *
     * @public
     *
     * @returns The hidden fields sorted by display order.
     */
    public getHiddenFields(): FormField<MaquinariaModel>[] {
        const hiddenFields: FormField<MaquinariaModel>[] = [
            {
                name: 'id',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                    defaultValue: this.getHiddenFieldDefaultValue('id'),
                },
                width: 0,
                conditions: this.conditions(),
                order: 1,
            },
            {
                name: 'id_cliente',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                    defaultValue: this.getHiddenFieldDefaultValue('id_cliente'),
                },
                width: 0,
                conditions: this.conditions({ isVisible: !this.showMaquinariaClienteAsignado || !this.isEditable() }),
                order: 2,
            },
            {
                name: 'id_comercio',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                    defaultValue: this.getHiddenFieldDefaultValue('id_comercio'),
                },
                width: 0,
                conditions: this.conditions({ isVisible: !this.isEditable() }),
                order: 3,
            },
            {
                name: 'adjunto',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                    defaultValue: this.getHiddenFieldDefaultValue('adjunto'),
                },
                updateValue: () => this.model.adjunto,
                width: 0,
                conditions: this.conditions(),
                order: 4,
            },
            {
                name: 'adjunto_pdf',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                    defaultValue: this.getHiddenFieldDefaultValue('adjunto_pdf'),
                },
                updateValue: () => this.model.adjunto_pdf,
                width: 0,
                conditions: this.conditions({ isVisible: this.showMaquinariaAdjuntoPdf }),
                order: 5,
            },
        ];

        return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getHiddenFields();

    /**
     * Builds and returns all machinery form sections.
     *
     * @private
     *
     * @returns The form sections sorted by display order.
     */
    private getFormSections(): FormSection<MaquinariaModel>[] {
        const sections: FormSection<MaquinariaModel>[] = [
            {
                name: '',
                fields: this.getMachineryFields(),
                width: 12,
                isVisible: true,
                order: 1,
            },
            {
                name: '',
                fields: this.getHiddenFields(),
                width: 12,
                isVisible: true,
                order: 2,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }// getFormSections();

    /**
     * Builds the root form metadata object.
     *
     * @private
     *
     * @param table - Optional table configuration used by table-based form modes.
     *
     * @returns The root form metadata object.
     */
    private buildForm(table: (string[] | string[][])[] | null): Form<MaquinariaModel> {
        return {
            name: 'maquinaria',
            header: this.getHeader(),
            type: this.formType,
            sections: this.getFormSections(),
            table: table === null
                ? null
                : { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
            uploadButton: { isEnabled: false, label: '' },
            uploadButtons: this.getUploadButtons(),
            buttons: [],
            signed: false,
        };
    }// buildForm();

    /**
     * Builds and returns the reactive form header upload buttons.
     *
     * File upload controls are rendered by `app-reactive-form-builder`, so the
     * machinery form no longer needs hidden file inputs or external upload buttons.
     * Existing attachments remain visible in informative and deletion modes through
     * the same upload component view action.
     *
     * @private
     *
     * @returns Upload buttons available for the current machinery form.
     */
    private getUploadButtons(): FormUploadButton[] {
        const uploadButtons: FormUploadButton[] = [];

        if (this.isEditable() || this.hasAttachedFile('adjunto')) {
            uploadButtons.push({
                isEnabled: true,
                label: 'Añadir foto',
                imageName: 'adjunto',
                imageFolder: 'fotos_maquinaria',
                imageDisplay: 'header',
                accept: 'image/*',
                allowedExtensions: ['gif', 'jpeg', 'jpg', 'jiff', 'png', 'svg', 'tiff'],
                invalidFormatMessage: 'Formato de imagen no válido.',
                uploadUrl: 'ws/trabajadores/upload_file.php',
                viewFolder: 'fotos_maquinaria',
                showPreview: false,
                idField: 'id',
            });
        }// if();

        if (this.showMaquinariaAdjuntoPdf && (this.isEditable() || this.hasAttachedFile('adjunto_pdf'))) {
            uploadButtons.push({
                isEnabled: true,
                label: 'Añadir documento',
                imageName: 'adjunto_pdf',
                imageFolder: 'fotos_maquinaria',
                imageDisplay: 'header',
                accept: '.doc,.docx,.pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf',
                allowedExtensions: ['doc', 'docx', 'pdf'],
                invalidFormatMessage: 'Formato de documento no válido.',
                uploadUrl: 'ws/trabajadores/upload_file.php',
                viewFolder: 'fotos_maquinaria',
                showPreview: false,
                idField: 'id',
            });
        }// if();

        return uploadButtons;
    }// getUploadButtons();

    /**
     * Indicates whether the current machinery model has a stored attachment.
     *
     * @private
     *
     * @param fieldName - Attachment field to inspect.
     *
     * @returns True when the attachment field contains a usable file name.
     */
    private hasAttachedFile(fieldName: 'adjunto' | 'adjunto_pdf'): boolean {
        return String(this.model[fieldName] ?? '').trim().length > 0;
    }// hasAttachedFile();

    /**
     * Creates field conditions using the project defaults.
     *
     * @private
     *
     * @param overrides - Partial condition values to override defaults.
     *
     * @returns A complete field conditions object.
     */
    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }// conditions();

    /**
     * Appends visible fields to table/export metadata arrays.
     *
     * @private
     *
     * @param fields - Fields to inspect and append when visible.
     *
     * @returns Nothing.
     */
    private appendVisibleFields(fields: FormField<MaquinariaModel>[]): void {
        const visibleFields = fields.filter((field) =>
            field.conditions.isVisible && field.input.type !== InputType.EMPTY && field.input.type !== InputType.HIDDEN,
        );

        this.fieldNames = this.fieldNames.concat(visibleFields);
        this.fieldLabels = this.fieldLabels.concat(visibleFields.map((field) => field.label));
    }// appendVisibleFields();

    /**
     * Returns the form header based on the active form mode.
     *
     * @private
     *
     * @returns Human-readable form header.
     */
    private getHeader(): string {
        switch (this.formType) {
            case FormType.CREATION:
                return 'Crear maquinaria';
            case FormType.DUPLICATION:
                return 'Duplicar maquinaria';
            case FormType.EDITION:
                return 'Editar maquinaria';
            default:
                return '';
        }
    }// getHeader();

    /**
     * Indicates whether normal fields can be edited in the current form mode.
     *
     * @private
     *
     * @returns True for creation, edition and duplication modes.
     */
    private isEditable(): boolean {
        return this.formType < FormType.DELETION;
    }// isEditable();

    /**
     * Returns the field name used for the owner depending on the form mode.
     *
     * @private
     *
     * @returns Backend id field in editable forms, display field in modal forms.
     */
    private getClientFieldName(): string {
        return this.isEditable() ? 'id_cliente' : 'cliente';
    }// getClientFieldName();

    /**
     * Returns the field name used for the supplier depending on the form mode.
     *
     * @private
     *
     * @returns Backend id field in editable forms, display field in modal forms.
     */
    private getCommerceFieldName(): string {
        return this.isEditable() ? 'id_comercio' : 'empresa';
    }// getCommerceFieldName();

    /**
     * Filters supplier dropdown values according to the active application mode.
     *
     * @private
     *
     * @param option - Dropdown option to evaluate.
     *
     * @returns True when the option must remain visible.
     */
    private filterCommerceOption(value: unknown): boolean {
        const commerce = value as Compradores | null | undefined;

        if (commerce === null || commerce === undefined) {
            return true;
        }// if();

        if (this.appName === 'sirga') {
            return commerce.tipo === 'Proveedor';
        }// if();

        if (this.activateREA) {
            return commerce.tipo === 'Servicios Agrícolas';
        }// if();

        return false;
    }// filterCommerceOption();

    /**
     * Returns the switch label used by applications that rename active ownership.
     *
     * @private
     *
     * @returns Active field label.
     */
    private getActiveLabel(): string {
        if (this.titleActivo !== 'Propio') {
            return this.titleActivo;
        }// if();

        return this.normalizeSwitchValue(this.model.activo) === 1 ? 'Propio' : 'Externo';
    }// getActiveLabel();

    /**
     * Normalizes fields that are sensitive to backend display formats.
     *
     * @private
     *
     * @returns Nothing.
     */
    private normalizeModel(): void {
        this.model.activo = this.normalizeSwitchValue(this.model.activo);

        if (this.appName === 'manezylozano') {
            this.model.mail_trat = this.normalizeSwitchValue(this.model.mail_trat);
        }// if();

        if (this.model.id_comercio === undefined) {
            this.model.id_comercio = null;
        }// if();
    }// normalizeModel();

    /**
     * Recalculates the ITEAF expiration date from purchase and inspection dates.
     *
     * @private
     *
     * @returns Nothing.
     */
    private calculateExpirationDate(): void {
        const acquisitionDate = String(this.model.fecha_adquisicion ?? '').trim();
        const inspectionDate = String(this.model.fecha_ultima_inspeccion ?? '').trim();

        if (!acquisitionDate && !inspectionDate) {
            this.model.fecha_caducidad = '';
            return;
        }// if();

        const acquisitionMoment = moment(acquisitionDate, 'DD/MM/YYYY', true);
        const inspectionMoment = moment(inspectionDate, 'DD/MM/YYYY', true);
        const baseMoment = inspectionMoment.isValid() ? inspectionMoment : acquisitionMoment;

        if (!baseMoment.isValid()) {
            this.model.fecha_caducidad = '';
            return;
        }// if();

        const age = acquisitionMoment.isValid() ? moment().year() - acquisitionMoment.year() : Number.NaN;
        const hasRecentInspectionRule = inspectionMoment.isValid() || age > 10 || Number.isNaN(age);
        const yearsToAdd = hasRecentInspectionRule && inspectionMoment.year() < 2020 ? 5 : hasRecentInspectionRule ? 3 : 10;

        this.model.fecha_caducidad = baseMoment.clone().add(yearsToAdd, 'years').format('DD/MM/YYYY');
    }// calculateExpirationDate();

    /**
     * Converts backend switch variants to the numeric values expected by PrimeNG.
     *
     * @private
     *
     * @param value - Raw switch value received from the form or backend.
     *
     * @returns `1` when enabled, otherwise `0`.
     */
    private normalizeSwitchValue(value: unknown): 1 | 0 {
        if (value === 1 || value === true) {
            return 1;
        }// if();

        if (typeof value === 'string') {
            const normalizedValue = value.trim().toLowerCase();

            return ['1', 'true', 'si', 'sí', 's', 'yes'].includes(normalizedValue) ? 1 : 0;
        }// if();

        return 0;
    }// normalizeSwitchValue();

    /**
     * Reads a scalar hidden field default value from the current model.
     *
     * @private
     *
     * @param name - Hidden field name.
     *
     * @returns The default value for the hidden field.
     */
    private getHiddenFieldDefaultValue(name: string): string | number | boolean | null | undefined {
        const value = (this.model as Record<string, unknown>)[name];

        if (value === null || value === undefined) {
            return null;
        }// if();

        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
            return value;
        }// if();

        return String(value);
    }// getHiddenFieldDefaultValue();
}// class;
